import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputAdornment,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import {
  ApiError, type ArcosTestResult, type OrgApiKeyIssued, type IntegrationCatalogEntry,
} from '@cmms/api-client'

import { api } from '../../lib/api'
import { APP_NAME } from '../../lib/branding'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { EndpointsPanel, DeviceEventsPanel } from './MPulsePanels'

export function IntegrationsTab() {
  const { isSuperAdmin } = usePermissions()
  const catalogQ = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn:  () => api.integrations.catalog.list(),
  })
  const catalog = useMemo(() => Array.isArray(catalogQ.data) ? catalogQ.data : [], [catalogQ.data])
  const [selected, setSelected] = useState<string>('arcos')
  const [editing, setEditing] = useState<IntegrationCatalogEntry | null>(null)

  return (
    <Stack spacing={3} sx={{ pb: 4 }}>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
        {catalogQ.isLoading ? (
          <Box sx={{ py: 2 }}><CircularProgress size={22} /></Box>
        ) : catalog.map((entry) => (
          <IntegrationCard
            key={entry.key}
            entry={entry}
            selected={selected === entry.key}
            onSelect={() => setSelected(entry.key)}
            onEdit={isSuperAdmin ? () => setEditing(entry) : undefined}
          />
        ))}
      </Stack>

      {selected === 'arcos' && <>
        <ArcosPanel />
        <ApiKeysPanel />
      </>}
      {selected === 'mpulse' && <>
        <EndpointsPanel />
        <DeviceEventsPanel />
      </>}

      <CatalogEditDialog entry={editing} onClose={() => setEditing(null)} />
    </Stack>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Integration picker — one card per catalog entry, super admins get an
// edit affordance to rename/re-icon it (platform-wide, not per-org).
// ═══════════════════════════════════════════════════════════════════════════

function IntegrationCard({ entry, selected, onSelect, onEdit }: {
  entry: IntegrationCatalogEntry
  selected: boolean
  onSelect: () => void
  onEdit?: () => void
}) {
  return (
    <Card
      onClick={onSelect}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.5, cursor: 'pointer', minWidth: 220,
        border: '1.5px solid', borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? (t) => `${t.palette.primary.main}0a` : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <IntegrationIcon iconUrl={entry.icon_url} size={34} />
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1, minWidth: 0 }}>
        {entry.display_name}
      </Typography>
      {onEdit && (
        <Tooltip title="Rename / change icon">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit() }}>
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Card>
  )
}

function CatalogEditDialog({ entry, onClose }: {
  entry: IntegrationCatalogEntry | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')

  useEffect(() => {
    if (entry) setName(entry.display_name)
  }, [entry])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['integrations', 'catalog'] })

  const rename = useMutation({
    mutationFn: (displayName: string) => api.integrations.catalog.rename(entry!.key, displayName),
    onSuccess: () => { toast.success('Renamed'); invalidate() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to rename'),
  })

  const uploadIcon = useMutation({
    mutationFn: (file: File) => api.integrations.catalog.uploadIcon(entry!.key, file),
    onSuccess: () => { toast.success('Icon updated'); invalidate() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to upload icon'),
  })

  const resetIcon = useMutation({
    mutationFn: () => api.integrations.catalog.resetIcon(entry!.key),
    onSuccess: () => { toast.success('Icon reset to default'); invalidate() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to reset icon'),
  })

  if (!entry) return null
  const busy = rename.isPending || uploadIcon.isPending || resetIcon.isPending

  return (
    <Dialog open={!!entry} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit integration</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IntegrationIcon iconUrl={entry.icon_url} size={56} />
          <Stack spacing={0.75}>
            <Button
              size="small" variant="outlined" component="label"
              startIcon={<PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />}
              disabled={busy}
            >
              {entry.icon_url ? 'Replace icon' : 'Upload icon'}
              <input
                type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) uploadIcon.mutate(file)
                  e.target.value = ''
                }}
              />
            </Button>
            {entry.icon_url && (
              <Button
                size="small" color="inherit" disabled={busy}
                startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => resetIcon.mutate()}
              >
                Reset to default
              </Button>
            )}
          </Stack>
        </Stack>

        <TextField
          size="small"
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
          helperText="Shown to every org on the platform as this integration's name."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">Close</Button>
        <Button
          variant="contained"
          size="small"
          disabled={!name.trim() || name.trim() === entry.display_name || rename.isPending}
          onClick={() => rename.mutate(name.trim())}
        >
          {rename.isPending ? 'Saving…' : 'Save name'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// PANEL A — Arcos outbound integration
// ═══════════════════════════════════════════════════════════════════════════

function ArcosPanel() {
  const qc = useQueryClient()
  const { formatDate } = useOrgSettings()
  const settingsQ = useQuery({
    queryKey: ['integrations', 'arcos'],
    queryFn:  () => api.integrations.arcos.get(),
  })
  const s = settingsQ.data

  const [endpoint, setEndpoint] = useState('')
  const [apiKey, setApiKey]     = useState('')
  const [showKey, setShowKey]   = useState(false)
  const [dirty, setDirty]       = useState(false)

  // Hydrate the endpoint from server data once; never overwrite typing.
  useEffect(() => {
    if (s && !dirty) setEndpoint(s.arcos_endpoint ?? '')
  }, [s, dirty])

  const save = useMutation({
    // Only send the key if the user typed one — sending null would wipe it.
    mutationFn: () => api.integrations.arcos.save({
      endpoint: endpoint || null,
      ...(apiKey ? { api_key: apiKey } : {}),
    }),
    onSuccess: (next) => {
      qc.setQueryData(['integrations', 'arcos'], next)
      setApiKey('')
      setShowKey(false)
      setDirty(false)
      toast.success('Arcos settings saved')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  const test = useMutation<ArcosTestResult>({
    mutationFn: () => api.integrations.arcos.test(),
    onSuccess: (r) => {
      qc.setQueryData(['integrations', 'arcos'], r.settings)
      if (r.ok) toast.success(`Connected to ${r.name ?? r.slug ?? 'Arcos'}`)
      else      toast.error(r.message ?? 'Arcos did not respond')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Test failed'),
  })

  const clearKey = useMutation({
    mutationFn: () => api.integrations.arcos.save({ api_key: null }),
    onSuccess: (next) => {
      qc.setQueryData(['integrations', 'arcos'], next)
      toast.success('Arcos API key cleared')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to clear'),
  })

  // Polling toggle is its own mutation so flipping the switch doesn't
  // need the user to also click Save — feels weird otherwise.
  const togglePolling = useMutation({
    mutationFn: (next: boolean) =>
      api.integrations.arcos.save({ iot_streaming_enabled: next }),
    onSuccess: (next) => {
      qc.setQueryData(['integrations', 'arcos'], next)
      toast.success(next.iot_streaming_enabled
        ? 'Live telemetry polling enabled'
        : 'Live telemetry polling paused')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update'),
  })

  if (settingsQ.isLoading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
  }

  const hasKey = !!s?.arcos_api_key_masked
  const lastTest = s?.arcos_last_test_at
    ? formatDate(s.arcos_last_test_at, true) : 'Never'

  return (
    <Card>
      <SectionHeader
        icon={<HubOutlinedIcon sx={{ fontSize: 20 }} />}
        title="Arcos IoT Platform"
        subtitle="Push assets to Arcos and pull live telemetry. Configure once per org."
      />
      <CardContent sx={{ pt: 0, px: 3.5, pb: 3.5 }}>
        <Stack spacing={2.5}>
          {/* Connection status banner */}
          {s?.arcos_org_slug && s?.arcos_last_test_ok && (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineIcon fontSize="small" />}
              sx={{ '& .MuiAlert-message': { fontSize: 13 } }}
            >
              Connected to <strong>{s.arcos_org_name ?? s.arcos_org_slug}</strong>
              {' · '}last verified {lastTest}
            </Alert>
          )}
          {s?.arcos_last_test_ok === false && (
            <Alert
              severity="error"
              icon={<ErrorOutlineIcon fontSize="small" />}
              sx={{ '& .MuiAlert-message': { fontSize: 13 } }}
            >
              Last test failed ({lastTest}). Check the endpoint and key, then retry.
            </Alert>
          )}

          <FieldStack
            label="Endpoint URL"
            helper="e.g. https://arcos.acme.com/bacs/api/v1"
          >
            <TextField
              size="small"
              fullWidth
              value={endpoint}
              onChange={(e) => { setEndpoint(e.target.value); setDirty(true) }}
              placeholder="https://arcos.example.com/bacs/api/v1"
            />
          </FieldStack>

          <FieldStack
            label="API key"
            helper={hasKey
              ? 'A key is configured. Paste a new value to replace it; leave blank to keep the current one.'
              : 'Paste the Arcos-issued key. It will be encrypted at rest and never displayed again.'}
          >
            <TextField
              size="small"
              fullWidth
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setDirty(true) }}
              type={showKey ? 'text' : 'password'}
              placeholder={hasKey ? s?.arcos_api_key_masked ?? '••••••••' : 'arcos_...'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowKey((v) => !v)} edge="end">
                      {showKey ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FieldStack>

          <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={save.isPending || !dirty}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!hasKey || !s?.arcos_endpoint || test.isPending || dirty}
              onClick={() => test.mutate()}
            >
              {test.isPending ? 'Testing…' : 'Test connection'}
            </Button>
            {hasKey && (
              <Button
                size="small"
                color="error"
                disabled={clearKey.isPending}
                onClick={() => clearKey.mutate()}
                sx={{ ml: 'auto' }}
              >
                Clear key
              </Button>
            )}
          </Stack>

          {dirty && (
            <Typography sx={{ fontSize: 12, color: 'warning.main' }}>
              Unsaved changes. Save before testing.
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                Fetch live telemetry from Arcos
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                When on, opening an asset's Telemetry tab pulls fresh point values from Arcos
                on each poll. When off, the tab shows the last cached values and {APP_NAME} makes no
                calls to Arcos — useful when Arcos is in maintenance.
              </Typography>
            </Box>
            <Switch
              checked={s?.iot_streaming_enabled ?? true}
              disabled={togglePolling.isPending}
              onChange={(e) => togglePolling.mutate(e.target.checked)}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// PANEL B — Inbound API keys (machine callers like Arcos use these)
// ═══════════════════════════════════════════════════════════════════════════

function ApiKeysPanel() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const { formatDate } = useOrgSettings()
  const keysQ = useQuery({
    queryKey: ['integrations', 'api-keys'],
    queryFn:  () => api.integrations.apiKeys.list(),
  })
  const keys = useMemo(() => Array.isArray(keysQ.data) ? keysQ.data : [], [keysQ.data])

  const [issuing, setIssuing] = useState(false)
  const [justIssued, setJustIssued] = useState<OrgApiKeyIssued | null>(null)

  const revoke = useMutation({
    mutationFn: (id: string) => api.integrations.apiKeys.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'api-keys'] })
      toast.success('Key revoked')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to revoke'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.integrations.apiKeys.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'api-keys'] })
      toast.success('Key deleted')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  return (
    <Card>
      <SectionHeader
        icon={<KeyOutlinedIcon sx={{ fontSize: 20 }} />}
        title="API keys"
        subtitle="Issue keys so Arcos (or other partners) can call this org's API."
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setIssuing(true)}
          >
            Issue new key
          </Button>
        }
      />
      <CardContent sx={{ pt: 0, px: 3.5, pb: 3.5 }}>
        {keysQ.isLoading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : keys.length === 0 ? (
          <Box sx={{
            py: 5, textAlign: 'center', border: '1px dashed',
            borderColor: 'divider', borderRadius: 2, color: 'text.secondary',
          }}>
            <Typography sx={{ fontSize: 13 }}>
              No API keys yet. Issue one to let Arcos talk to this workspace.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Prefix</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Scopes</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Last used</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((k) => {
                const revoked = !!k.revoked_at
                return (
                  <TableRow key={k.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>{k.name}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontFamily: 'monospace', color: 'text.secondary' }}>
                      {k.key_prefix}…
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {k.scopes.map((s) => (
                          <Chip key={s} size="small" label={s.replace('integration:', '')} sx={{ fontSize: 11, height: 20 }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                      {k.last_used_at ? formatDate(k.last_used_at, true) : 'Never'}
                    </TableCell>
                    <TableCell>
                      {revoked
                        ? <Chip size="small" label="Revoked" color="default" sx={{ fontSize: 11, height: 20 }} />
                        : <Chip size="small" label="Active"  color="success" sx={{ fontSize: 11, height: 20 }} />}
                    </TableCell>
                    <TableCell align="right">
                      {!revoked && (
                        <Tooltip title="Revoke">
                          <span>
                            <IconButton
                              size="small"
                              disabled={revoke.isPending}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: 'Revoke this key?',
                                  message: 'The integration using it will start receiving 401s immediately. This cannot be undone.',
                                  confirmLabel: 'Revoke',
                                  variant: 'danger',
                                })
                                if (ok) revoke.mutate(k.id)
                              }}
                            >
                              <BlockOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            disabled={remove.isPending}
                            onClick={async () => {
                              const ok = await confirm({
                                title: 'Delete this key row?',
                                message: 'Removes the record entirely. Revoke first if the key might still be in use.',
                                confirmLabel: 'Delete',
                                variant: 'danger',
                              })
                              if (ok) remove.mutate(k.id)
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <IssueKeyDialog
        open={issuing}
        onClose={() => setIssuing(false)}
        onIssued={(k) => {
          setIssuing(false)
          setJustIssued(k)
          qc.invalidateQueries({ queryKey: ['integrations', 'api-keys'] })
        }}
      />

      <ShowKeyDialog
        keyData={justIssued}
        onClose={() => setJustIssued(null)}
      />
    </Card>
  )
}

// ─── Dialogs ────────────────────────────────────────────────────────────────

function IssueKeyDialog({ open, onClose, onIssued }: {
  open: boolean
  onClose: () => void
  onIssued: (k: OrgApiKeyIssued) => void
}) {
  const [name, setName] = useState('')
  const [read, setRead]   = useState(true)
  const [write, setWrite] = useState(true)

  // Reset form whenever the dialog opens.
  useEffect(() => {
    if (open) { setName(''); setRead(true); setWrite(true) }
  }, [open])

  const issue = useMutation({
    mutationFn: () => api.integrations.apiKeys.issue({
      name: name.trim(),
      scopes: [
        ...(read ? ['integration:read' as const] : []),
        ...(write ? ['integration:write' as const] : []),
      ],
    }),
    onSuccess: (k) => onIssued(k),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to issue key'),
  })

  const canSubmit = name.trim().length > 0 && (read || write) && !issue.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Issue a new API key</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <TextField
            size="small"
            label="Name"
            placeholder="e.g. Arcos production"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
              Scopes
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label="read"
                color={read ? 'primary' : 'default'}
                variant={read ? 'filled' : 'outlined'}
                onClick={() => setRead((v) => !v)}
                size="small"
              />
              <Chip
                label="write"
                color={write ? 'primary' : 'default'}
                variant={write ? 'filled' : 'outlined'}
                onClick={() => setWrite((v) => !v)}
                size="small"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          onClick={() => issue.mutate()}
          disabled={!canSubmit}
          variant="contained"
          size="small"
        >
          {issue.isPending ? 'Issuing…' : 'Issue key'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ShowKeyDialog({ keyData, onClose }: {
  keyData: OrgApiKeyIssued | null
  onClose: () => void
}) {
  const copy = () => {
    if (!keyData) return
    navigator.clipboard.writeText(keyData.plaintext_key)
      .then(() => toast.success('Copied'))
      .catch(() => toast.error('Copy failed — select and copy manually'))
  }

  return (
    <Dialog open={!!keyData} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Save this key — it won't be shown again
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: 13 } }}>
            Copy the key now. We only store its hash; reopening this dialog will
            not reveal it again. If you lose it, issue a new key and revoke this one.
          </Alert>
          <TextField
            size="small"
            fullWidth
            value={keyData?.plaintext_key ?? ''}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: 13 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={copy}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onFocus={(e) => e.target.select()}
          />
          <Divider />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Scopes:</Typography>
            {keyData?.scopes.map((s) => (
              <Chip key={s} size="small" label={s.replace('integration:', '')} sx={{ fontSize: 11, height: 20 }} />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" size="small">I have saved it</Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Small layout helpers ───────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle, action }: {
  icon: React.ReactNode
  title: string
  subtitle?: string
  action?: React.ReactNode
}) {
  return (
    <Box sx={{ px: 3.5, pt: 3, pb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 2, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main',
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>
        )}
      </Box>
      {action}
    </Box>
  )
}

function FieldStack({ label, helper, children }: {
  label: string
  helper?: string
  children: React.ReactNode
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      {children}
      {helper && (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>{helper}</Typography>
      )}
    </Box>
  )
}
